<script setup>
import { JContentDetails } from 'media-flow-ui'
import { storeToRefs } from 'pinia'
import { useSignalrBuilder } from '~/stores/signalr-builder'
import { usePageBuilder } from '~/stores/page-builder'
import { useUrlBuilder } from '~/stores/url-builder'
import { useAuthBuilder } from '~~/stores/auth-builder'
import { formatDate } from '~~/utils/formatDate'
import {
  formatContentEngagementsStyle,
  formatContentComponentStyle,
} from '~/models/layout/media/ContentDetails'
import { formatIconStyle } from '~/helpers/icon/icon'
import { $$t } from '~~/utils/i19m'

const props = defineProps({
  data: {
    type: Object,
    required: false,
    default: false,
  },
})

const authBuilderStore = useAuthBuilder()

const signalrBuilderStore = useSignalrBuilder()
const { realTimeMediaViews } = storeToRefs(signalrBuilderStore)

const pageBuilderStore = usePageBuilder()
const urlBuilderStore = useUrlBuilder()

const { mediaEngagements, getLayoutMediaData, deviceType } =
  storeToRefs(pageBuilderStore)
const { getProjectId, getProjectUrl } = urlBuilderStore

const { data } = props
const origin = ref('')
const pathname = ref('')
const viewsCount = ref(0)

const engagements = computed(
  () => data.components.engagements || data.engagements
)
const engagementsStyle = computed(() =>
  formatContentEngagementsStyle(engagements.value.style || {})
)
const titleStyle = computed(() =>
  formatContentComponentStyle(data.components.title?.style || {})
)
const descriptionStyle = computed(() =>
  formatContentComponentStyle(data.components.description?.style || {})
)
const style = computed(() => formatContentComponentStyle(data.style || {}))

const likeIcon = computed(() =>
  engagements.value.props.like.icon
    ? formatIconStyle(engagements.value.props.like.icon)
    : undefined
)
const dislikeIcon = computed(() =>
  engagements.value.props.dislike.icon
    ? formatIconStyle(engagements.value.props.dislike.icon)
    : undefined
)
const shareIcon = computed(() =>
  engagements.value.props.share.icon
    ? formatIconStyle(engagements.value.props.share.icon)
    : undefined
)

const removeLike = () => {
  if (!mediaEngagements.value.hasLiked) return

  mediaEngagements.value.likesCount -= 1
  mediaEngagements.value.hasLiked = false
}

const removeDislike = () => {
  if (!mediaEngagements.value.hasDisliked) return

  mediaEngagements.value.dislikesCount -= 1
  mediaEngagements.value.hasDisliked = false
}

const addDislike = () => {
  if (mediaEngagements.value.hasDisliked) return

  mediaEngagements.value.dislikesCount += 1
  mediaEngagements.value.hasDisliked = true

  removeLike()
}

const addLike = () => {
  if (mediaEngagements.value.hasLiked) return

  mediaEngagements.value.likesCount += 1
  mediaEngagements.value.hasLiked = true

  removeDislike()
}

const onEngagementInteractionCallback = async (engagementTypeId) => {
  // console.log("onEngagementInteractionCallback", engagementTypeId);

  const requiresLogin = await authBuilderStore.actionRequireLogin()
  if (requiresLogin) return

  switch (engagementTypeId) {
    case 1:
      if (mediaEngagements.value?.hasLiked) {
        //remove like
        pageBuilderStore.actionDeleteUserEngagement(engagementTypeId, 2)
        removeLike()
      } else {
        //add like
        pageBuilderStore.actionPostUserEngagement(engagementTypeId, 2)
        addLike()
      }

      break
    case 2:
      if (mediaEngagements.value.hasDisliked) {
        //remove dislike
        pageBuilderStore.actionDeleteUserEngagement(engagementTypeId, 2)
        removeDislike()
      } else {
        //add dislike
        pageBuilderStore.actionPostUserEngagement(engagementTypeId, 2)
        addDislike()
      }

      break

    default:
      console.log('engagement not handeled')
      break
  }
}

const mediaViewsCount = computed(() => {
  if (realTimeMediaViews.value > 0) {
    return realTimeMediaViews.value
  }

  try {
    return Number(viewsCount.value).toLocaleString('en-US')
  } catch (error) {
    console.error(error)
  }

  return viewsCount.value
})

//p-0

// :viewsCount="
//         realTimeMediaViews > 0
//           ? realTimeMediaViews
//           : getLayoutMediaData?.viewCount?.toLocaleString('en-US')
//       "

onMounted(async () => {
  pageBuilderStore.actionGetUserEngagement()

  if (data.props.componentsList.views) {
    const response = await pageBuilderStore.actionGetMediaViewsCount()
    viewsCount.value = response
  }

  origin.value = window.location.origin
  pathname.value = window.location.pathname
})
</script>

<template>
  <div class="j-details">
    <!-- {{ mediaEngagements }} -->
    <!-- <ClientOnly> -->

    <JContentDetails
      v-if="mediaEngagements"
      :callback="onEngagementInteractionCallback"
      :title="getLayoutMediaData?.title"
      :description="getLayoutMediaData?.description"
      :origin="origin"
      :pathname="pathname"
      :viewsCount="viewsCount"
      :img="
        getLayoutMediaData?.imageAssets?.length
          ? getLayoutMediaData.imageAssets[0]?.url
          : ''
      "
      :date="formatDate(getLayoutMediaData?.startDate)"
      :duration="getLayoutMediaData?.length"
      :likesCount="
        engagements.props.like.type === 1 ? mediaEngagements?.likesCount : null
      "
      :dislikesCount="
        engagements.props.dislike.type === 1
          ? mediaEngagements?.dislikesCount
          : null
      "
      :favoritesCount="mediaEngagements?.favoritesCount"
      :subscribesCount="mediaEngagements?.subscribesCount"
      :watchLaterCount="mediaEngagements?.watchLaterCount"
      :hasLiked="mediaEngagements?.hasLiked"
      :hasDisliked="mediaEngagements?.hasDisliked"
      :hasFavorite="mediaEngagements?.hasFavorite"
      :hasSubsribed="mediaEngagements?.hasSubsribed"
      :hasWatchLater="mediaEngagements?.hasWatchLater"
      :showMoreText="$$t('component_details_show_more')"
      :showLessText="$$t('component_details_show_less')"
      :shareText="$$t('component_details_share')"
      :viewsCountText="
        realTimeMediaViews > 0
          ? $$t('component_details_views_online_count')
          : $$t('component_details_views_count')
      "
      :videoStartTimeText="$$t('component_details_video_start_time')"
      :copyText="$$t('component_details_copy')"
      :linkText="$$t('component_details_link')"
      :engagementsShowDemo="getProjectId !== 'lGqEarlNem9'"
      :breakpoint="deviceType"
      :style="style"
      :engagements-style="engagementsStyle"
      :title-style="titleStyle"
      :description-style="descriptionStyle"
      :show-title="data.props.componentsList.title"
      :show-description="data.props.componentsList.description"
      :show-publish-date="data.props.componentsList.publishDate"
      :show-views="data.props.componentsList.views && viewsCount > 0"
      :show-like="engagements.props.like.type !== 3"
      :show-dislike="engagements.props.dislike.type !== 3"
      :show-share="engagements.props.share.type !== 3"
      :show-like-icon="engagements.props.like.buttonType !== 'text'"
      :show-like-text="engagements.props.like.buttonType !== 'icon'"
      :show-dislike-icon="engagements.props.dislike.buttonType !== 'text'"
      :show-dislike-text="engagements.props.dislike.buttonType !== 'icon'"
      :show-share-icon="engagements.props.share.buttonType !== 'text'"
      :show-share-text="engagements.props.share.buttonType !== 'icon'"
      :like-icon="likeIcon"
      :dislike-icon="dislikeIcon"
      :share-icon="shareIcon"
      :isVerticalPlayer="getLayoutMediaData?.player?.isVertical || false"
    ></JContentDetails>
    <!-- </ClientOnly> -->
  </div>
</template>
